import { z } from "zod";
import {
  EquipmentKind,
  MovementPattern,
  Muscle,
  MuscleRegion,
} from "./schemas/muscle.js";
import taxonomyJson from "./data/muscle-taxonomy.json" with { type: "json" };

// Pipeline output shape (Haiku agents 1+2). Validated at module load so
// any schema drift in the bundled JSON surfaces during build, not at
// runtime when a coach clicks "Import taxonomy".
const TaxonomyExercise = z.object({
  slug: z.string().min(1),
  name_en: z.string().min(1),
  primary: z.array(z.string()).min(1).max(8),
  secondary: z.array(z.string()).max(12).default([]),
  isBodyweight: z.boolean(),
  hasP1RM: z.boolean(),
  movementPattern: MovementPattern,
  equipment: EquipmentKind,
  aliases: z.array(z.string().min(1).max(120)).max(20).optional(),
});

const TaxonomyFile = z.object({
  version: z.number(),
  generatedAt: z.string(),
  muscles: z.array(Muscle).min(1),
  exercises: z.array(TaxonomyExercise).min(1),
});

const parsed = TaxonomyFile.parse(taxonomyJson);

// Build slug → Muscle index for fast lookup at the UI layer.
const muscleBySlug = new Map(parsed.muscles.map((m) => [m.slug, m]));

export type Taxonomy = z.infer<typeof TaxonomyFile>;
export type TaxonomyExercise = z.infer<typeof TaxonomyExercise>;

export const taxonomy: Taxonomy = parsed;

// Region helper: callers pick the language at the call site so we don't
// drag i18n into the shared package.
export const muscleName = (
  slug: string,
  lang: "en" | "it" | "fr" | "de" | "ru",
): string => {
  const m = muscleBySlug.get(slug);
  if (!m) return slug;
  return m.names[lang] ?? m.names.en;
};

export const muscleRegion = (slug: string): MuscleRegion | null =>
  muscleBySlug.get(slug)?.region ?? null;

// The bulk taxonomy import doesn't stamp `region` on exercise rows —
// region lives on the muscle, not the exercise. Derive it on the read
// path from the first primary muscle so the catalog filter, picker
// filter, and section grouping all work without forcing a re-import.
//
// Kept on the shared package because both web (ExerciseCatalog) and
// mobile (ExercisePicker, CatalogScreen) need the same derivation —
// duplicating it splits the fix when the rule shifts.
export const exerciseRegion = (ex: {
  region?: MuscleRegion;
  primary?: string[];
  movementPattern?: string;
}): MuscleRegion | null => {
  if (ex.region) return ex.region;
  // Cardio is treated as a pseudo-region for the picker chip even
  // though it isn't anatomical (1.66.10). movementPattern must win
  // REGARDLESS of `primary`, so a cardio row always renders as the
  // pseudo-region instead of an anatomical one.
  // ⛔ The old reason given here — "most cardio rows leave `primary`
  // empty" — was false and is retracted (2026-09-14): all 17 cardio
  // rows carry `primary` AND `muscleContrib`. Same stale claim was
  // retracted in volumeByMuscle.ts; don't reintroduce it.
  if (ex.movementPattern === "cardio") return "cardio";
  const slug = ex.primary?.[0];
  if (!slug) return null;
  return muscleRegion(slug);
};

export const muscleSlugs = (): string[] => parsed.muscles.map((m) => m.slug);
